/**
 * @file StraightsContractModal.js
 * @version 1.17.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-24
 * @modified 2026-10-28
 * @description Straights pricing contract modal — parent-level fields (customer,
 *              product, contract_number, supplier, delivery terms, dock) +
 *              an editable months grid (year/month, qty, cost, price).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContractPricing.css';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import ApplyScopeControl from './ApplyScopeControl';
import ComboBox from './ComboBox';
import { addressLine1 } from '../utils/formatAddress';
import { defaultContractDeliveryTerms } from '../utils/deliveryTerms';
import {
  isHoldingProduct, holdingProductOption, productValueFor, productPayloadFor, HoldingProductDescription
} from './HoldingProduct';
import OurContractNumberField from './OurContractNumberField';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDecimalInput } from '../utils/numberFormatters';
import LinkedPurchaseOrdersPanel from './LinkedPurchaseOrdersPanel';
import ContractOrdersPanel from './ContractOrdersPanel';
import { autoSelectSupplierFromItemCode, getProductCost } from '../utils/pricingHelpers';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const DELIVERY_TERMS_OPTIONS = [
  { value: '', label: 'Select delivery terms…' },
  { value: 'Direct Delivery', label: 'Direct Delivery' },
  { value: 'Delivery', label: 'Delivery' },
  { value: 'Customer Collection', label: 'Customer Collection' }
];

function StraightsContractModal({ contract, mode: initialMode = 'edit', onClose, onSave }) {
  // Local mode state so the footer Edit button can flip view → edit
  // without re-mounting the modal.
  const [mode, setMode] = useState(initialMode);
  useSlideOutButton([mode]);
  const [closing, setClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [reps, setReps] = useState([]);
  const [existingDocks, setExistingDocks] = useState([]);
  // True while the initial reference-data fetch is in flight. Drives the
  // body-overlay LoadingSpinner so the user sees progress instead of an
  // empty form while the customer / product / supplier / rep lists load.
  const [isInitializing, setIsInitializing] = useState(true);

  const [formData, setFormData] = useState({
    account_id: '',
    inventory_item_id: '',
    // The description of a holding product (inventory_item_id = HOLDING_PRODUCT).
    holding_product_name: '',
    supplier_contact_id: '',
    // Straights go direct from the supplier to the farm.
    delivery_terms: contract ? '' : defaultContractDeliveryTerms({ categoryId: 1 }),
    dock: '',
    rep_name: '',
    notes: '',
    contract_number: '', // single Excel Agri ↔ customer reference (parent-level)
    is_draft: false,
    is_fulfilled_override: false
  });
  const [months, setMonths] = useState([]);

  // Pricing-group apply scope (parent + pricing-linked children). Prefilled when editing.
  const [pricingGroup, setPricingGroup] = useState([]);
  const [applyScope, setApplyScope] = useState(
    ['parent', 'group', 'specific'].includes(contract?.apply_scope) ? contract.apply_scope : 'parent'
  );
  const [scopeAccountIds, setScopeAccountIds] = useState(
    Array.isArray(contract?.scope_accounts) ? contract.scope_accounts.map(a => a.id) : []
  );

  // --- loaders --------------------------------------------------------------
  useEffect(() => {
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) contentWrapper.classList.add('modal-open');

    const load = async () => {
      const t0 = performance.now();
      try {
        // The last endpoint used to be /api/pricing/straights-contracts —
        // the full view that powers the Straights table itself, just so we
        // could pluck distinct dock strings out of it. Replaced with a
        // dedicated lightweight /docks endpoint (SELECT DISTINCT dock).
        const [custRes, prodRes, suppRes, repRes, dockRes] = await Promise.all([
          fetch(`${API_URL}/api/contacts/search?limit=10000&contactStatus=all&accountType=CUSTOMER`, { credentials: 'include' }),
          fetch(`${API_URL}/api/pricing/inventory-items`, { credentials: 'include' }),
          fetch(`${API_URL}/api/contacts/search?limit=10000&contactStatus=all&accountType=SUPPLIER`, { credentials: 'include' }),
          fetch(`${API_URL}/api/contacts/sales-reps`, { credentials: 'include' }),
          fetch(`${API_URL}/api/pricing/straights-contracts/docks`, { credentials: 'include' })
        ]);
        const cust = await custRes.json();
        const prod = await prodRes.json();
        const supp = await suppRes.json();
        const repJson = await repRes.json();
        const dockJson = await dockRes.json();
        setCustomers(cust.contacts || cust.data || cust || []);
        // Straights sales_account_code = '204'
        const allItems = Array.isArray(prod) ? prod : (prod.items || prod.data || []);
        setProducts(allItems.filter(p => p.sales_account_code === '204'));
        setSuppliers(supp.contacts || supp.data || supp || []);
        setReps((repJson.reps || []).map(r => r.name));
        setExistingDocks(dockJson.docks || []);
        console.log(`[StraightsContractModal] reference-data load: ${Math.round(performance.now() - t0)}ms`);
      } catch (e) {
        console.error('Failed to load reference data', e);
      } finally {
        setIsInitializing(false);
      }
    };
    load();

    if (contract) {
      setFormData({
        account_id: contract.account_id != null ? contract.account_id.toString() : '',
        inventory_item_id: productValueFor(contract),
        holding_product_name: contract.holding_product_name ?? '',
        supplier_contact_id: contract.supplier_contact_id != null ? contract.supplier_contact_id.toString() : '',
        delivery_terms: contract.delivery_terms ?? '',
        dock: contract.dock ?? '',
        rep_name: contract.rep_name ?? '',
        notes: contract.notes ?? '',
        contract_number: contract.contract_number ?? '',
        is_draft: !!contract.is_draft,
        // Contract-level override forces the parent + every month to show
        // FULFILLED regardless of qty maths or per-month overrides.
        is_fulfilled_override: !!contract.is_fulfilled_override
      });
      setMonths((contract.months || []).map(m => {
        // Each month carries a SUPPLIER contract reference (per-month so
        // different suppliers can issue different references). The new
        // column is supplier_contract_number; older data still lives in the
        // legacy contract_numbers text[] (used by the auto-match view).
        // Fall back to the legacy value so a freshly-loaded form shows
        // the existing reference, then save will rewrite both columns.
        const legacy = Array.isArray(m.contract_numbers)
          ? m.contract_numbers.filter(Boolean).join(', ')
          : (m.contract_number || '');
        const supplier = m.supplier_contract_number ?? legacy ?? '';
        return {
          id: m.id,
          year: m.year,
          month: m.month,
          // 2dp minimum, up to 4 where the figure has them ("236.125", not the
          // column's "236.1250"); blank stays ''. Save parses all three.
          quantity: formatDecimalInput(m.quantity),
          cost: formatDecimalInput(m.cost),
          price: formatDecimalInput(m.price),
          supplier_contract_number: supplier,
          // Dock is per-month — a contract can discharge at a different port
          // each month. Months saved before the field existed carry the
          // contract-level value (the service used to mirror it down), so
          // there's nothing to fall back to here.
          dock: m.dock ?? '',
          is_fulfilled_override: !!m.is_fulfilled_override
        };
      }));
    } else {
      // New contract — seed with one empty month row using current year / month
      const now = new Date();
      setMonths([{ year: now.getFullYear(), month: now.getMonth() + 1, quantity: '', cost: '', price: '', supplier_contract_number: '', dock: '', is_fulfilled_override: false }]);
    }

    return () => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  // Load the selected customer's pricing group so the "Applies to" control can
  // offer Whole group / Parent only / Specific. Empty (control hidden) when not
  // in a pricing group.
  useEffect(() => {
    const accountId = formData.account_id;
    if (!accountId) { setPricingGroup([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/contacts/pricing-group/${accountId}`, { credentials: 'include' });
        if (!res.ok) { if (!cancelled) setPricingGroup([]); return; }
        const data = await res.json();
        if (cancelled) return;
        const members = data.members || [];
        setPricingGroup(members);
        if (members.length <= 1) { setApplyScope('parent'); setScopeAccountIds([]); }
      } catch (e) { if (!cancelled) setPricingGroup([]); }
    })();
    return () => { cancelled = true; };
  }, [formData.account_id]);

  // --- helpers --------------------------------------------------------------
  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onClose(), 200);
  };

  const handleChange = (field, value) => {
    if (error) setError('');
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Clear dock when delivery terms is anything other than 'Delivery'
      if (field === 'delivery_terms' && value !== 'Delivery') next.dock = '';
      // Auto-fill rep from the selected customer (user can override afterwards)
      if (field === 'account_id' && value) {
        const selectedCustomer = customers.find(c => c.id.toString() === value);
        if (selectedCustomer && selectedCustomer.sales_rep) {
          next.rep_name = selectedCustomer.sales_rep;
        }
      }
      // Auto-fill default supplier when product is selected (matches the
      // ContractPricingModal behaviour for blends/compounds/spot).
      if (field === 'inventory_item_id' && value) {
        const selectedProduct = products.find(p => p.id.toString() === value);
        if (selectedProduct) {
          const supplier = autoSelectSupplierFromItemCode(selectedProduct.item_code, suppliers);
          if (supplier && !next.supplier_contact_id) {
            next.supplier_contact_id = supplier.id.toString();
          }
        }
      }
      return next;
    });

    // Auto-fill cost on every month row that's currently blank when the
    // product is selected. Months the user has already typed a cost into
    // are left alone.
    if (field === 'inventory_item_id' && value) {
      const selectedProduct = products.find(p => p.id.toString() === value);
      const cost = getProductCost(selectedProduct);
      if (cost !== null) {
        setMonths(prev => prev.map(m => (
          m.cost === '' || m.cost === null || m.cost === undefined
            ? { ...m, cost }
            : m
        )));
      }
    }
  };

  const dockVisible = formData.delivery_terms === 'Delivery';

  const addMonth = () => {
    const last = months[months.length - 1];
    let nextYear = last ? last.year : new Date().getFullYear();
    let nextMonth = last ? last.month + 1 : 1;
    if (nextMonth > 12) { nextMonth = 1; nextYear += 1; }
    // Pre-fill the new month with the previous month's contract numbers —
    // mirrors the previous "duplicate the string" behaviour but works at
    // the array level. The pending field always starts empty.
    setMonths(prev => [...prev, {
      year: nextYear,
      month: nextMonth,
      quantity: '',
      cost: '',
      price: '',
      supplier_contract_number: last?.supplier_contract_number ?? '',
      // Carry the previous month's dock forward — the port usually doesn't
      // change month to month, and the copy-to-all button covers the rest.
      dock: last?.dock ?? '',
      is_fulfilled_override: false
    }]);
  };

  const removeMonth = (idx) => {
    setMonths(prev => prev.filter((_, i) => i !== idx));
  };

  const updateMonth = (idx, field, value) => {
    setMonths(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  // Overwrite the dock on every month with `value` (including a blank), so one
  // shared port can be applied in a click. Same shape as the blend modal's
  // supplier-contract copy button.
  const applyDockToAll = (value) => {
    setMonths(prev => prev.map(m => ({ ...m, dock: value })));
  };

  const margin = (m) => {
    const c = parseFloat(m.cost) || 0;
    const p = parseFloat(m.price) || 0;
    if (!c && !p) return '';
    return p - c;
  };

  // --- validation + save ----------------------------------------------------
  const validate = () => {
    if (!formData.account_id) return 'Select a customer.';
    if (!formData.inventory_item_id) return 'Select a product.';
    if (isHoldingProduct(formData.inventory_item_id) && !(formData.holding_product_name || '').trim()) {
      return 'Describe the holding product — what the contract is for.';
    }
    if (months.length === 0) return 'Add at least one month.';
    if (months.some(m => m.year == null || m.month == null)) return 'Every month row needs a year and month.';
    // Duplicate (year, month) check within this contract's rows
    const seen = new Set();
    for (const m of months) {
      const key = `${m.year}-${m.month}`;
      if (seen.has(key)) return `Duplicate month: ${MONTH_NAMES[m.month - 1]} ${m.year}. Remove the duplicate row.`;
      seen.add(key);
    }
    return null;
  };

  const handleSave = async (asDraftOverride = null) => {
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true);
    setError('');
    try {
      const payload = {
        account_id: parseInt(formData.account_id, 10),
        ...(() => {
          const p = productPayloadFor(formData.inventory_item_id, formData.holding_product_name);
          return { ...p, inventory_item_id: p.inventory_item_id ? parseInt(p.inventory_item_id, 10) : null };
        })(),
        supplier_contact_id: formData.supplier_contact_id ? parseInt(formData.supplier_contact_id, 10) : null,
        delivery_terms: formData.delivery_terms || null,
        dock: dockVisible ? (formData.dock || null) : null,
        rep_name: formData.rep_name || null,
        notes: formData.notes || null,
        // Our contract number now lives at the parent level — single string.
        contract_number: formData.contract_number || null,
        is_fulfilled_override: !!formData.is_fulfilled_override,
        is_draft: asDraftOverride != null ? !!asDraftOverride : !!formData.is_draft,
        apply_scope: applyScope,
        specific_account_ids: applyScope === 'specific' ? scopeAccountIds : null,
        months: months.map(m => ({
          id: m.id || null,
          year: parseInt(m.year, 10),
          month: parseInt(m.month, 10),
          quantity: m.quantity === '' ? null : parseFloat(m.quantity),
          cost: m.cost === '' ? null : parseFloat(m.cost),
          price: m.price === '' ? null : parseFloat(m.price),
          // Mirror the supplier contract number into BOTH columns so the
          // legacy auto-match view (which still keys off contract_numbers)
          // and the new supplier_contract_number column stay in sync until
          // the view is migrated off the legacy column. The text[] column
          // is split on '/' and ',' so historical entries like
          // "SC19913/ PO-21141" become ['SC19913','PO-21141'] — the view
          // matches each element individually against PO description /
          // reference / delivery_instructions.
          // Bug history: an earlier version cleared contract_numbers to []
          // unconditionally, silently wiping the supplier reference for any
          // straights contract that was re-saved before this mirroring was
          // added.
          contract_numbers: (m.supplier_contract_number || '')
            .split(/[,/]+/)
            .map(s => s.trim())
            .filter(Boolean),
          supplier_contract_number: (m.supplier_contract_number || '').trim() || null,
          // Send '' rather than omitting the key when the dock is cleared —
          // the service falls back to the contract-level dock on `undefined`,
          // so omitting it would quietly resurrect the value the user deleted.
          dock: dockVisible ? (m.dock || '').trim() : '',
          is_fulfilled_override: !!m.is_fulfilled_override
        }))
      };

      const url = contract && contract.id
        ? `${API_URL}/api/pricing/straights-contracts/${contract.id}`
        : `${API_URL}/api/pricing/straights-contracts`;
      const method = contract && contract.id ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Failed to save');

      // Pass the saved straights contract id back so the parent table can
      // refresh just that one row instead of reloading the whole list.
      const savedId = data?.id || contract?.id || null;
      onSave && onSave({ straightsContractId: savedId });
      handleClose();
    } catch (e) {
      setError(e.message || 'Save failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // --- render ---------------------------------------------------------------
  const customerOptions = useMemo(() => [
    { value: '', label: 'Select customer…' },
    ...customers.map(c => {
      const addr = addressLine1(c.address);
      return {
        value: c.id.toString(),
        badge: c.account_no || '',
        label: c.company_name || c.name,
        renderLabel: (<>{c.company_name || c.name}{addr && <span className="address-line"> ({addr})</span>}</>),
        searchText: `${c.account_no || ''} ${c.company_name || c.name || ''} ${addr}`
      };
    })
  ], [customers]);

  const productOptions = useMemo(() => [
    { value: '', label: 'Select product…' },
    holdingProductOption(),
    ...products.map(p => ({
      value: p.id.toString(),
      badge: p.item_code || '',
      label: p.item_name,
      searchText: `${p.item_code || ''} ${p.item_name || ''}`
    }))
  ], [products]);

  const supplierOptions = useMemo(() => [
    { value: '', label: 'None' },
    ...suppliers.map(s => ({
      value: s.id.toString(),
      badge: s.account_no || '',
      label: s.company_name || s.name,
      searchText: `${s.account_no || ''} ${s.company_name || s.name || ''}`
    }))
  ], [suppliers]);

  const repOptions = useMemo(() => [
    { value: '', label: 'Select Rep…' },
    ...reps.map(r => ({ value: r, label: r }))
  ], [reps]);

  const dockOptions = useMemo(() => {
    const preset = ['Portbury', 'Teignmouth', 'Plymouth', 'Portland', 'Newport', 'Southampton', 'Avonmouth'];
    const set = new Set(preset);
    existingDocks.forEach(d => { if (d) set.add(d); });
    return [
      { value: '', label: 'Select dock…' },
      ...Array.from(set).sort().map(d => ({ value: d, label: d }))
    ];
  }, [existingDocks]);

  const isView = mode === 'view';
  // Straights are the one place a cost may be overridden per contract: the value
  // auto-fills from the product's current cost, but an edit here is stored
  // against this contract's month row only. It never writes back to the
  // inventory item, so the item's cost-price-last-updated stamp is unaffected.
  const costOverrideTitle = 'Overrides the cost for this contract only — does not change the inventory item cost';

  // "Applies to" control — only shown when the selected customer is in a pricing
  // group. Lets the straights price apply to the whole group, just the parent,
  // or an explicit subset. Hidden for single-account customers.
  const renderApplyScope = () => {
    if (pricingGroup.length <= 1) return null;
    return (
      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
        <ApplyScopeControl
          members={pricingGroup}
          accountId={formData.account_id}
          applyScope={applyScope}
          scopeAccountIds={scopeAccountIds}
          onScopeChange={setApplyScope}
          onAccountsChange={setScopeAccountIds}
          onSelectAccount={(id) => handleChange('account_id', String(id))}
          readOnly={isView}
        />
      </div>
    );
  };

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content compound-pricing modal-container ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="contract-panel-left" style={{ width: 900 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>{contract ? (isView ? 'View Straights Contract' : 'Edit Straights Contract') : 'New Straights Contract'}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-lg)' }}>
            {/* Contract-level fulfilled override — forces the whole contract
                (every month) to show FULFILLED. Lives in the header alongside
                the close button. */}
            <div className="modal-fulfilled-toggle">
              <label
                htmlFor="straights-contract-fulfilled-override"
                className="toggle-text"
                title="Force the whole contract (every month) to show as FULFILLED regardless of qty/used maths or per-month overrides."
                style={{ cursor: isView ? 'default' : 'pointer' }}
              >
                Mark Whole Contract Fulfilled
              </label>
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    id="straights-contract-fulfilled-override"
                    type="checkbox"
                    checked={!!formData.is_fulfilled_override}
                    onChange={(e) => handleChange('is_fulfilled_override', e.target.checked)}
                    disabled={isView}
                  />
                  <label htmlFor="straights-contract-fulfilled-override"></label>
                </div>
              </div>
            </div>
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        <div className="modal-body" style={{ padding: '0 var(--spacing-xl) var(--spacing-xl)', position: 'relative' }}>
          {isInitializing && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(var(--bg-main-rgb), 0.85)', zIndex: 5 }}>
              <LoadingSpinner />
            </div>
          )}
          {error && (
            <div style={{ padding: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)', background: 'rgba(var(--color-error-rgb), 0.1)', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-md)', color: 'var(--color-error)', fontSize: 'var(--font-sm)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <span className="material-symbols-rounded">error</span>
              <span>{error}</span>
            </div>
          )}

          <div className="form-grid">
            {/* Row 1 — Customer (wide), Rep, Our Contract No. */}
            <div className="form-row-4" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
              <div className="form-group">
                <label>Customer *</label>
                <SearchableDropdown
                  value={formData.account_id}
                  onChange={(v) => handleChange('account_id', v)}
                  options={customerOptions}
                  placeholder="Select customer…"
                  searchPlaceholder="Search by # or name…"
                  disabled={isView}
                />
              </div>

              <div
                className="form-group"
                title="The rep is taken from the selected customer's contact record. Edit it there if it needs changing."
              >
                <label>Rep</label>
                <SearchableDropdown
                  value={formData.rep_name}
                  onChange={(v) => handleChange('rep_name', v)}
                  options={repOptions}
                  placeholder="Select Rep…"
                  searchPlaceholder="Search reps…"
                  disabled
                />
              </div>

              <OurContractNumberField
                value={formData.contract_number}
                onChange={(v) => handleChange('contract_number', v)}
                disabled={isView}
              />
            </div>

            {renderApplyScope()}

            {/* Row 2 — Product, Supplier. */}
            <div className="form-row-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="form-group">
                <label>Product *</label>
                <SearchableDropdown
                  value={formData.inventory_item_id}
                  onChange={(v) => handleChange('inventory_item_id', v)}
                  options={productOptions}
                  placeholder="Select product…"
                  searchPlaceholder="Search by code or name…"
                  disabled={isView}
                />
                {isHoldingProduct(formData.inventory_item_id) && (
                  <HoldingProductDescription
                    id="straights-holding-product"
                    value={formData.holding_product_name}
                    onChange={(v) => handleChange('holding_product_name', v)}
                    disabled={isView}
                  />
                )}
              </div>

              <div className="form-group">
                <label>Supplier</label>
                <SearchableDropdown
                  value={formData.supplier_contact_id}
                  onChange={(v) => handleChange('supplier_contact_id', v)}
                  options={supplierOptions}
                  placeholder="Select supplier…"
                  searchPlaceholder="Search…"
                  disabled={isView}
                />
              </div>
            </div>

            {/* Row 3 — Delivery Terms, Dock (when delivery), Notes. */}
            <div className="form-row-4" style={{ gridTemplateColumns: dockVisible ? '1fr 1fr 2fr' : '1fr 2fr' }}>
              <div className="form-group">
                <label>Delivery Terms</label>
                <Dropdown
                  value={formData.delivery_terms}
                  onChange={(v) => handleChange('delivery_terms', v)}
                  options={DELIVERY_TERMS_OPTIONS}
                  placeholder="Select…"
                  disabled={isView}
                />
              </div>

              {dockVisible && (
                <div className="form-group">
                  <label>Depot / Port / Dock *</label>
                  <ComboBox
                    value={formData.dock}
                    onChange={(v) => handleChange('dock', v)}
                    options={dockOptions}
                    placeholder="Select or type dock…"
                    searchPlaceholder="Search…"
                    disabled={isView}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  disabled={isView}
                  rows={2}
                  style={{ width: '100%', padding: 'var(--spacing-sm) var(--spacing-md)', border: '1px solid var(--border-secondary)', borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', marginTop: 'var(--spacing-lg)', paddingTop: 'var(--spacing-xl)', borderTop: '1px solid var(--border-secondary)' }}>
              <h4 style={{ margin: '0 0 var(--spacing-sm)', color: 'var(--text-secondary)' }}>Months</h4>
              {months.map((m, idx) => {
                const marg = margin(m);
                return (
                  <div key={idx} className="product-group-line" style={{ padding: 'var(--spacing-md)', borderRadius: 'var(--radius-lg)', background: 'rgba(var(--bg-main-rgb), 0.5)', marginBottom: 'var(--spacing-sm)', position: 'relative' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: dockVisible ? '1.5fr 1fr 2fr 1.5fr' : '1.5fr 1fr 2fr', gap: 'var(--spacing-md)' }}>
                      <div className="form-group">
                        <label>Month *</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Dropdown
                            value={m.month?.toString()}
                            onChange={(v) => updateMonth(idx, 'month', parseInt(v, 10))}
                            options={MONTH_NAMES.map((n, i) => ({ value: (i + 1).toString(), label: n }))}
                            disabled={isView}
                          />
                          <Input
                            type="number"
                            value={m.year}
                            onChange={(e) => updateMonth(idx, 'year', parseInt(e.target.value, 10))}
                            disabled={isView}
                            style={{ width: 90 }}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Quantity</label>
                        <Input type="number" step="0.01" value={m.quantity} onChange={(e) => updateMonth(idx, 'quantity', e.target.value)} disabled={isView} placeholder="0" />
                      </div>
                      <div className="form-group">
                        <label>Supplier Contract</label>
                        <Input
                          type="text"
                          value={m.supplier_contract_number || ''}
                          onChange={(e) => updateMonth(idx, 'supplier_contract_number', e.target.value)}
                          disabled={isView}
                          placeholder="Supplier's contract #"
                        />
                      </div>
                      {/* Per-month dock — a contract can discharge at a
                          different port each month. Gated on Delivery terms to
                          match the contract-level field above; the copy button
                          applies this month's port to every month at once. */}
                      {dockVisible && (
                        <div className="form-group">
                          <label>Depot / Port / Dock</label>
                          <div style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <ComboBox
                                value={m.dock || ''}
                                onChange={(v) => updateMonth(idx, 'dock', v)}
                                options={dockOptions}
                                placeholder="Select or type dock…"
                                searchPlaceholder="Search…"
                                disabled={isView}
                              />
                            </div>
                            <button
                              type="button"
                              className="btn-icon"
                              title="Apply this dock to all months (overwrites all, including blank)"
                              onClick={() => applyDockToAll(m.dock || '')}
                              disabled={isView}
                            >
                              <span className="material-symbols-rounded">content_copy</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-sm)' }}>
                      <div className="form-group">
                        <label>Cost (£)</label>
                        <Input type="number" step="0.01" value={m.cost} onChange={(e) => updateMonth(idx, 'cost', e.target.value)} disabled={isView} title={costOverrideTitle} placeholder="0" />
                      </div>
                      <div className="form-group">
                        <label>Price (£)</label>
                        <Input type="number" step="0.01" value={m.price} onChange={(e) => updateMonth(idx, 'price', e.target.value)} disabled={isView} placeholder="0" />
                      </div>
                      <div className="form-group">
                        <label>Margin (£)</label>
                        <Input type="text" value={marg === '' ? '' : formatCurrency(marg)} placeholder="Auto" disabled />
                      </div>
                      {/* Per-month fulfilled toggle. Outside .form-group so the
                          .modal-content .form-group label rule doesn't shrink the
                          empty toggle <label> to ~12px and collapse the slider. */}
                      <div className="fulfilled-toggle-cell">
                        <span
                          className="fulfilled-toggle-label"
                          title="Force this month to show as FULFILLED in tables regardless of qty/used maths."
                        >Mark Fulfilled</span>
                        <div className="checkbox-container">
                          <div className="check light">
                            <input
                              id={`fulfilled-override-${idx}`}
                              type="checkbox"
                              checked={!!m.is_fulfilled_override}
                              onChange={(e) => updateMonth(idx, 'is_fulfilled_override', e.target.checked)}
                              disabled={isView}
                            />
                            <label htmlFor={`fulfilled-override-${idx}`}></label>
                          </div>
                        </div>
                      </div>
                    </div>
                    {!isView && months.length > 1 && (
                      <button type="button" className="btn-icon btn-danger" onClick={() => removeMonth(idx)}
                        style={{ position: 'absolute', top: '8px', right: '8px' }} title="Remove month">
                        <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>close</span>
                      </button>
                    )}
                    {/* Per-month linked-POs panel — only for existing monthly_pricing
                        rows. Each month is its own straights contract (different
                        contract_numbers per period). */}
                    {m.id && (
                      <LinkedPurchaseOrdersPanel
                        kind="monthly"
                        entityId={m.id}
                        mode={mode}
                        title={`Linked Purchase Orders — ${m.month}/${m.year}`}
                      />
                    )}
                    {m.id && (
                      <ContractOrdersPanel
                        set={{ kind: 'monthly', ids: [m.id] }}
                        canEdit={mode !== 'view'}
                        title={`Orders counted against ${m.month}/${m.year}`}
                      />
                    )}
                  </div>
                );
              })}
              {!isView && (
                <button type="button" className="btn-full" onClick={addMonth}
                  style={{ margin: '0 auto', display: 'flex' }} title="Add month">
                  <span className="icon-wrapper">
                    <span className="material-symbols-rounded">add</span>
                  </span>
                  <span className="btn-label">Add Month</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          {!isView && (
            <>
              <div>
                <button type="button" className="btn-slideout" onClick={() => handleSave()} disabled={loading}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">{loading ? 'sync' : 'save'}</span></div>
                  <span className="btn-label">{loading ? 'Saving…' : (contract ? 'Update' : 'Save')}</span>
                </button>
              </div>
              <div>
                <button
                  type="button"
                  className={`btn-slideout draft-toggle-btn ${formData.is_draft ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, is_draft: !prev.is_draft }))}
                  disabled={loading}
                  title="Drafts are excluded from price lookups, allocation and remaining-qty until promoted to live."
                >
                  <div className="icon-wrapper"><span className="material-symbols-rounded">{formData.is_draft ? 'drafts' : 'publish'}</span></div>
                  <span className="btn-label">{formData.is_draft ? 'Saving as Draft' : 'Save as Draft'}</span>
                </button>
              </div>
            </>
          )}
          <div style={{ width: '100%', textAlign: 'right' }}>
            {isView ? (
              <button type="button" className="btn-slideout btn-slideout-primary" onClick={() => setMode('edit')}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">edit</span></div>
                <span className="btn-label">Edit</span>
              </button>
            ) : (
              <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel</span>
              </button>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default StraightsContractModal;
